import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle, ArrowRight, Eye, EyeOff, Phone, ArrowLeft } from 'lucide-react';
import './Auth.css';

function Login({ onSwitchToSignup, onBack, onForgotPassword }) {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  
  const { sendLoginOTP, setupRecaptcha, clearRecaptcha } = useAuth();

  const resetForm = useCallback(() => {
    clearRecaptcha();
    setOtpSent(false);
    setOtp('');
    setConfirmationResult(null);
    setError('');
    setResendTimer(0);
  }, [clearRecaptcha]);

  useEffect(() => {
    return () => {
      clearRecaptcha();
    };
  }, [clearRecaptcha]);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const formatPhoneNumber = (number) => {
    const cleaned = number.replace(/\D/g, '');
    return cleaned.slice(0, 10);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!phone || phone.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    if (!password || password.length < 6) {
      setError('Please enter your password (min 6 characters)');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Setup and render recaptcha
      const recaptchaVerifier = await setupRecaptcha('recaptcha-container');
      const fullPhone = '+91' + phone;
      const confirmation = await sendLoginOTP(fullPhone, password, recaptchaVerifier);
      
      setConfirmationResult(confirmation);
      setOtpSent(true);
      setResendTimer(30);
    } catch (err) {
      console.error('Send OTP error:', err);
      clearRecaptcha();
      
      if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else if (err.code === 'auth/invalid-phone-number') {
        setError('Invalid phone number format.');
      } else if (err.code === 'auth/quota-exceeded') {
        setError('SMS quota exceeded. Please try again tomorrow.');
      } else if (err.message?.includes('billing')) {
        setError('Phone auth requires Firebase Blaze plan. Please contact admin.');
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await confirmationResult.confirm(otp);
      // Auth state change will handle redirect
    } catch (err) {
      console.error('Verify OTP error:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid OTP. Please check and try again.');
      } else if (err.code === 'auth/code-expired') {
        setError('OTP expired. Please request a new one.');
        resetForm();
      } else {
        setError('Failed to verify OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = () => {
    if (resendTimer > 0) return;
    resetForm();
  };

  const handleBack = () => {
    if (otpSent) {
      resetForm();
    } else if (onBack) {
      onBack();
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        {otpSent && (
          <button className="auth-back-btn" onClick={handleBack}>
            <ArrowLeft size={18} />
            <span>Back</span>
          </button>
        )}

        <div className="auth-header">
          <div className="auth-logo-container">
            <img src="/logo.png" alt="LatDiagnostics" className="auth-logo" />
          </div>
          <p className="auth-brand">LatDiagnostics</p>
          <p className="auth-tagline">Medical Diagnostic Platform</p>
        </div>

        <div className="auth-title">
          <h1>Welcome Back</h1>
          <p>Sign in to continue your consultation</p>
        </div>

        {error && (
          <div className="auth-error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {!otpSent ? (
          <form onSubmit={handleSendOTP} className="auth-form">
            <div className="form-group">
              <label>MOBILE NUMBER</label>
              <div className="phone-input-wrapper">
                <div className="country-code">
                  <span>IN +91</span>
                  <span className="dropdown-arrow">▼</span>
                </div>
                <input
                  type="tel"
                  placeholder="9876543210"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                  disabled={loading}
                  className="phone-input"
                  maxLength={10}
                />
              </div>
            </div>

            <div className="form-group">
              <label>PASSWORD</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  className="auth-input"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div id="recaptcha-container"></div>

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={20} className="spin" />
                  Sending OTP...
                </>
              ) : (
                <>
                  Send OTP
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="auth-form">
            <div className="otp-sent-message">
              <Phone size={20} />
              <span>OTP sent to +91 {phone}</span>
            </div>

            <div className="form-group">
              <label>ENTER OTP</label>
              <input
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={loading}
                className="auth-input otp-input"
                maxLength={6}
                autoFocus
              />
            </div>

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={20} className="spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify & Login
                  <ArrowRight size={20} />
                </>
              )}
            </button>

            <div className="resend-otp-section">
              {resendTimer > 0 ? (
                <span className="resend-timer">Resend OTP in {resendTimer}s</span>
              ) : (
                <button type="button" className="resend-otp-btn" onClick={handleResendOTP} disabled={loading}>
                  Resend OTP
                </button>
              )}
            </div>
          </form>
        )}

        <div className="auth-divider">
          <span>or</span>
        </div>

        <div className="auth-footer">
          <button onClick={onSwitchToSignup} className="switch-auth-btn">
            New here? Create an Account
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;
